import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BrandIcon from '../assets/DWA_WolfHead_Favicon_Web.png';

const DARK_COLOR = '#444444';
const ACCENT_COLOR = '#fec35c';

type AuthMode = 'login' | 'register';

export const AuthPage: React.FC = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    setIsLoading(true);

    try {
      if (mode === 'login') {
        await login({ email, password });
      } else {
        await register({
          email,
          password,
          display_name: displayName || undefined,
          access_code: accessCode || undefined
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError(null);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(to top right, #000000 0%, #313131 100%)',
      fontFamily: 'Lato, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '1rem'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '4px',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15)',
        padding: '2.5rem',
        overflow: 'hidden'
      }}>
        {/* Logo and Title */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div
            className="brand-logo-circle brand-logo-circle-md"
            style={{ marginBottom: '1rem' }}
          >
            <img
              src={BrandIcon}
              alt="Lupa"
            />
          </div>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: DARK_COLOR,
            margin: 0,
            marginBottom: '0.25rem'
          }}>
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p style={{
            fontSize: '0.875rem',
            color: '#666',
            margin: 0
          }}>
            {mode === 'login'
              ? 'Sign in to continue to Lupa'
              : 'Sign up to get started with Lupa'}
          </p>
          {/* Beta banner */}
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: 'rgba(254, 195, 92, 0.1)',
            borderRadius: '4px',
            border: `1px solid ${ACCENT_COLOR}`,
          }}>
            <p style={{
              fontSize: '0.75rem',
              color: ACCENT_COLOR,
              margin: 0,
              fontWeight: 500,
            }}>
              Lupa is currently in beta
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <>
              {/* Access Code Field */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: DARK_COLOR,
                  marginBottom: '0.5rem'
                }}>
                  Beta Access Code
                </label>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter the access code"
                  required
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '4px',
                    border: '2px solid rgba(229, 231, 235, 0.5)',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = ACCENT_COLOR}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(229, 231, 235, 0.5)'}
                />
              </div>
              {/* Display Name Field */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: DARK_COLOR,
                  marginBottom: '0.5rem'
                }}>
                  Display Name (optional)
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '4px',
                    border: '2px solid rgba(229, 231, 235, 0.5)',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = ACCENT_COLOR}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(229, 231, 235, 0.5)'}
                />
              </div>
            </>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: DARK_COLOR,
              marginBottom: '0.5rem'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                borderRadius: '4px',
                border: '2px solid rgba(229, 231, 235, 0.5)',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = ACCENT_COLOR}
              onBlur={(e) => e.target.style.borderColor = 'rgba(229, 231, 235, 0.5)'}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: DARK_COLOR,
              marginBottom: '0.5rem'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              minLength={6}
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                borderRadius: '4px',
                border: '2px solid rgba(229, 231, 235, 0.5)',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = ACCENT_COLOR}
              onBlur={(e) => e.target.style.borderColor = 'rgba(229, 231, 235, 0.5)'}
            />
          </div>

          {error && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#dc2626',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: ACCENT_COLOR,
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s, transform 0.1s',
              opacity: isLoading ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = '#f16952';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = ACCENT_COLOR;
            }}
          >
            {isLoading
              ? (mode === 'login' ? 'Signing in...' : 'Creating account...')
              : (mode === 'login' ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        {/* Toggle mode */}
        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center'
        }}>
          <span style={{ fontSize: '0.875rem', color: '#666' }}>
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          </span>
          <button
            onClick={toggleMode}
            style={{
              background: 'none',
              border: 'none',
              color: ACCENT_COLOR,
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 0
            }}
          >
            {mode === 'login' ? 'Sign up' : 'Sign in'}
          </button>
        </div>

        {/* Password Reset - Coming Soon */}
        {mode === 'login' && (
          <div style={{
            marginTop: '1rem',
            textAlign: 'center'
          }}>
            <button
              onClick={() => alert('Password reset coming soon!')}
              style={{
                background: 'none',
                border: 'none',
                color: '#999',
                fontSize: '0.75rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              Forgot your password?
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
